<template>
    <!-- Single-site point picker: Area > Zone > Point. It loads its own tree for
         `companyId` (the site in the URL by default), so a caller only binds
         v-model. Any other attribute (expanded-by-default, search-debounce, ...)
         falls through to TreeSelect. -->
    <div    v-if="loadError"
            class="flex items-center justify-between rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
        <span>Unable to load points.</span>
        <button type="button"
                class="font-medium text-blue-700 hover:text-blue-900"
                @click="load"
        >Retry</button>
    </div>
    <tree-select    v-else
                    :model-value="modelValue"
                    :nodes="nodes"
                    :loading="loading"
                    search-placeholder="Search areas, zones, or points"
                    :item-noun="{one: 'point', many: 'points'}"
                    empty-title="No points available"
                    empty-text="No points are available on this site."
                    no-match-title="No matching points"
                    @update:model-value="$emit('update:modelValue', $event)"
    ></tree-select>
</template>

<script>
import {areasToNodes, usePointTree} from "@/composables/usePointTree"

export default {
    name: "PointSelectTree",
    emits: ['update:modelValue', 'loaded'],
    props: {
        // Selected meter ids.
        modelValue: {
            type: Array,
            default: () => ([]),
        },
        // The site to load. Defaults to the site in the URL.
        companyId: {
            type: String,
            default: null,
        },
        // Adds each point's device serial to the `loaded` tree, for callers that count devices.
        withSerial: {
            type: Boolean,
            default: false,
        },
        // Escape hatch for the playground: [{ id, name, zones: [{ id, name, meters: [{ id, name }] }] }].
        // When set, nothing is loaded.
        tree: {
            type: Array,
            default: null,
        },
    },
    data() {
        return {
            areas: [],
            loading: false,
            loadError: false,
            // Ignores a late response after companyId changed mid-request.
            requestId: 0,
        }
    },
    computed: {
        nodes() {
            return Object.freeze(areasToNodes(this.tree ?? this.areas, '', (meter) => meter.id))
        },
    },
    watch: {
        companyId() {
            this.load()
        },
    },
    created() {
        this.load()
    },
    methods: {
        load() {
            if (this.tree !== null) {
                return
            }

            const requestId = ++this.requestId
            this.loading = true
            this.loadError = false

            usePointTree().fetchSiteTree(this.companyId, {withSerial: this.withSerial})
                .then((areas) => {
                    if (requestId !== this.requestId) {
                        return
                    }
                    // Frozen: Vue skips deep reactivity on 10,000 points it never needs to watch.
                    this.areas = Object.freeze(areas)
                    this.$emit('loaded', areas)
                })
                .catch(() => {
                    if (requestId === this.requestId) {
                        this.loadError = true
                    }
                })
                .finally(() => {
                    if (requestId === this.requestId) {
                        this.loading = false
                    }
                })
        },
    },
}
</script>
